(function () {

  angular.module('WorkflowProvider', [])

    .factory('WorkflowFormModel', function (DataSource, MetadataCustomFieldType, MetadataCustomFieldValidationType) {
      return function () {

        // SETTINGS SECTION

        this.name = '';
        this.active = false;
        this.bypassOutputCreation = false;
        this.availability = 'ALL';
        this.groups = [];

        // SOURCE SECTION

        this.source = 'MFP';

        // IMAGE PROCESSING SECTION

        const DEFAULT_IMAGE_PROCESSING = {
          crop: false,
          deleteBlank: false,
          deskew: false,
          rotation: 'AUTO'
        };

        this.imageProcessing = angular.copy(DEFAULT_IMAGE_PROCESSING);

        // OUTPUT DOCUMENT SECTION

        const DEFAULT_OCR_CONFIGURATION = {
          languages: ['ENGLISH'],
          tradeoff: 'ACCURACY'
        };

        this.fileType = 'GENERATE_SEARCHABLE_PDF';
        this.ocrConfiguration = angular.copy(DEFAULT_OCR_CONFIGURATION);

        /** @type {boolean} Indicates if image-only PDF should be generated as output document. */
        this.isImageOnlyPdf = false;

        // METADATA SECTION

        this.metadataProfile = {
          id: null,
          displayName: null,
          metadataFields: []
        };

        // DESTINATION SECTION

        this.destination = 'SEND_TO_EMAIL';
        this.fileName = '{WORKFLOW_NAME}-{YYYY_MM_DD}';

        // ACTIVITY SOURCE

        this.SCAN_FILES = {
          type: 'SCAN_FILES',
          paperSize: 'AUTO',
          pageOrientation: 'PORTRAIT',
          duplexMode: false,
          continuousJob: false,
          paperSource: 'AUTO',
          dotsPerInch: 300,
          color: 'GREY'
        };

        this.UPLOAD_FILES = {
          type: 'UPLOAD_FILES'
        };

        // ACTIVITY GROUP FILE TYPE

        this.GENERATE_SEARCHABLE_PDF = {
          type: 'GENERATE_SEARCHABLE_PDF',
          pdfCompatibility: 'PDF_1_6',
          imageQuality: 'MEDIUM'
        };

        this.GENERATE_DOCX = {
          type: 'GENERATE_DOCX',
          imageQuality: 'MEDIUM'
        };

        // ACTIVITY GROUP DESTINATION

        this.SEND_TO_EMAIL = {
          type: 'SEND_TO_EMAIL',
          to: [],
          cc: [],
          bcc: [],
          // signOff: '',
          subject: '',
          from: '',
          scanner: {
            to: true,
            cc: false,
            bcc: false
          },
          filenameConfiguration: {
            template: ''
          }
        };

        this.SEND_TO_ONEDRIVE = {
          type: 'SEND_TO_DESTINATION',
          filenameConfiguration: {
            template: ''
          },
          filepathConfiguration: {
            template: '/{WORKFLOW_NAME}/'
          },
          destinationProfile: null,
          destinationProfileData: null
        };

        this.SEND_TO_SHAREPOINT = {
          type: 'SEND_TO_DESTINATION',
          filenameConfiguration: {
            template: ''
          },
          filepathConfiguration: {
            template: '/{WORKFLOW_NAME}/'
          },
          destinationProfile: null,
          destinationProfileData: null
        };

        this.SEND_TO_CONNECTOR = {
          type: 'SEND_TO_DESTINATION',
          filenameConfiguration: {
            template: ''
          },
          filepathConfiguration: {
            template: '/{WORKFLOW_NAME}/'
          },
          destinationProfile: null,
          destinationProfileData: null,
          timeout: 10
        };

        this.SEND_TO_DESTINATION = {
          type: 'SEND_TO_DESTINATION',
          filenameConfiguration: {
            template: ''
          },
          filepathConfiguration: {
            template: ''
          },
          destinationProfile: null
        };

        // ACTIVITY GROUP INTERACTIVE

        this.INTERACTIVE = {
          type: 'INTERACTIVE',
          metadataProfileId: undefined,
          metadataFile: undefined // { type: 'JSON' }
        };

        // This object stores values that indicate when fields have validation errors
        this.errors = {};

        this.setData = (info) => {
          this.name = info.name;
          this.active = info.active;
          this.bypassOutputCreation = info._embedded.workflowActivities.find(activity => activity.type === "GENERATE_SEARCHABLE_PDF" || activity.type === "GENERATE_DOCX") === undefined;
          this.availability = info.groups.length ? 'GROUPS' : 'ALL';
          this.groups = info.groups;

          this.source = info.sources[0];

          this.fileName = info.fileName;

          // Take the "imageProcessing" / "ocrConfiguration" property from the first activity where it's present.
          // Use the default settings, if the properties are not present in any workflow activities.

          const imageProcessingData = info._embedded.workflowActivities.find(activity => 'imageProcessing' in activity)?.imageProcessing;
          this.imageProcessing = imageProcessingData ?? angular.copy(DEFAULT_IMAGE_PROCESSING);

          const ocrConfigurationData = info._embedded.workflowActivities.find(activity => 'ocrConfiguration' in activity)?.ocrConfiguration;
          this.ocrConfiguration = ocrConfigurationData ?? angular.copy(DEFAULT_OCR_CONFIGURATION);
          if (!this.ocrConfiguration.tradeoff) {
            this.ocrConfiguration.tradeoff = DEFAULT_OCR_CONFIGURATION.tradeoff;
          }

          const activityTypes = info._embedded.workflowActivities.map(activity => activity.type);

          if (activityTypes.includes('SCAN_FILES')) {
            this.SCAN_FILES = this.populateActivity('SCAN_FILES', info);
            switch (this.SCAN_FILES.duplexMode) {
              case 'TOP_TO_TOP':
                this.SCAN_FILES.duplexMode = true;
                break;
              case 'SIMPLEX':
                this.SCAN_FILES.duplexMode = false;
                break;
            }
          }

          if (activityTypes.includes('UPLOAD_FILES')) {
            this.UPLOAD_FILES = this.populateActivity('UPLOAD_FILES', info);
          }

          if (activityTypes.includes('GENERATE_SEARCHABLE_PDF')) {
            this.fileType = 'GENERATE_SEARCHABLE_PDF';
            this.isImageOnlyPdf = !ocrConfigurationData;
            this.GENERATE_SEARCHABLE_PDF = this.populateActivity('GENERATE_SEARCHABLE_PDF', info);
          }

          if (activityTypes.includes('GENERATE_DOCX')) {
            this.fileType = 'GENERATE_DOCX';
            this.isImageOnlyPdf = undefined;
            this.GENERATE_DOCX = this.populateActivity('GENERATE_DOCX', info);
          }

          if (activityTypes.includes('SEND_TO_EMAIL')) {
            this.destination = 'SEND_TO_EMAIL';
            this.SEND_TO_EMAIL = this.populateActivity('SEND_TO_EMAIL', info);
          }

          if (activityTypes.includes('SEND_TO_DESTINATION')) {
            const activityData = info._embedded.workflowActivities.find(activity => activity.type === 'SEND_TO_DESTINATION');
            switch (activityData._embedded.destinationProfile.type) {
              case 'OneDriveForBusiness':
                info.destinations = ['ONEDRIVE'];
                this.destination = 'SEND_TO_ONEDRIVE';
                this.SEND_TO_ONEDRIVE = this.populateActivity('SEND_TO_DESTINATION', info);
                this.SEND_TO_ONEDRIVE.destinationProfile = activityData._embedded.destinationProfile._links.self.href;
                this.SEND_TO_ONEDRIVE.destinationProfileData = activityData._embedded.destinationProfile;
                break;
              case 'SharePoint':
                info.destinations = ['SHAREPOINT'];
                this.destination = 'SEND_TO_SHAREPOINT';
                this.SEND_TO_SHAREPOINT = this.populateActivity('SEND_TO_DESTINATION', info);
                this.SEND_TO_SHAREPOINT.destinationProfile = activityData._embedded.destinationProfile._links.self.href;
                this.SEND_TO_SHAREPOINT.destinationProfileData = activityData._embedded.destinationProfile;
                break;
              case 'Connector':
                info.destinations = ['CONNECTOR'];
                this.destination = 'SEND_TO_CONNECTOR';
                this.SEND_TO_CONNECTOR = this.populateActivity('SEND_TO_DESTINATION', info);
                this.SEND_TO_CONNECTOR.destinationProfile = activityData._embedded.destinationProfile._links.self.href;
                this.SEND_TO_CONNECTOR.destinationProfileData = activityData._embedded.destinationProfile;
                this.SEND_TO_CONNECTOR.timeout = new Duration(activityData.timeout).toMinutes();
                break;
            }
          }

          if (activityTypes.includes('INTERACTIVE')) {
            this.populateActivity('INTERACTIVE', info);
            if (this.INTERACTIVE.metadataFile) {
              this.INTERACTIVE.metadataFile = this.INTERACTIVE.metadataFile.type;
            }

            const interactiveActivity = info._embedded.workflowActivities.find(activity => activity.type === 'INTERACTIVE');
            const metadataProfile = interactiveActivity._embedded.metadataProfile;
            if (metadataProfile) {
              this.metadataProfile.id = metadataProfile.id;
              this.metadataProfile.displayName = metadataProfile.displayName;
              this.metadataProfile.metadataFields = metadataProfile._embedded.customFields
                .map(customField => convertMetadataCustomFieldResponseToMetadataField(customField));
            }

            this.INTERACTIVE.metadataProfileId = this.metadataProfile.id;
          }

          return this;
        };

        function convertMetadataCustomFieldResponseToMetadataField(metadataCustomFieldResponse) {
          const metadataField = {
            id: metadataCustomFieldResponse.id,
            name: metadataCustomFieldResponse.name,
            label: metadataCustomFieldResponse.displayName,
            required: metadataCustomFieldResponse.required,
            defaultValue: metadataCustomFieldResponse.defaultValue
          };

          if (metadataCustomFieldResponse.type === MetadataCustomFieldType.Date) {
            metadataField.type = MetadataCustomFieldType.Text;
            metadataField.validationType = MetadataCustomFieldType.Date;
            metadataField.dateFormat = metadataCustomFieldResponse.format;
          } else {
            metadataField.type = metadataCustomFieldResponse.type;
            metadataField.validationType = metadataCustomFieldResponse.validation ? metadataCustomFieldResponse.validation.type : '';
            if (metadataField.type === MetadataCustomFieldType.Lookup) {
              metadataField.linkRecords = metadataCustomFieldResponse._embedded.dataSource._links['px:records'].href;
              metadataField.dataSource = new DataSource().fromDataSourceResponse(metadataCustomFieldResponse._embedded.dataSource);
            } else if (metadataField.validationType === MetadataCustomFieldValidationType.Regex) {
              metadataField.regularExpression = metadataCustomFieldResponse.validation.pattern;
            } else if (metadataField.validationType === MetadataCustomFieldValidationType.Numeric) {
              metadataField.decimals = metadataCustomFieldResponse.validation.maxDigits;
            }
          }

          return metadataField;
        }

        /**
         * A class representation of an ISO-8601 duration.
         *
         * @param {String} durationString An ISO-8601 duration string in the following format: P{number}Y{number}M{number}W{number}DT{number}H{number}M{number}S
         */
        function Duration(durationString) {
          const digits = '\\d+';
          const datePattern = `(${digits}Y)?(${digits}M)?(${digits}W)?(${digits}D)?`;
          const timePattern = `T(${digits}H)?(${digits}M)?(${digits}S)?`;
          const iso8601 = `P(?:${datePattern}(?:${timePattern})?)`;
          const pattern = new RegExp(iso8601);
          const matches = durationString.match(pattern);
          if (!matches || matches.length !== 8 || matches.filter(value => value != null).length === 0) {
            throw new RangeError(`Invalid duration: ${durationString}`);
          }

          this.years = parseFloat(matches[1] || '0') || 0;
          this.months = parseFloat(matches[2] || '0') || 0;
          this.weeks = parseFloat(matches[3] || '0') || 0;
          this.days = parseFloat(matches[4] || '0') || 0;
          this.hours = parseFloat(matches[5] || '0') || 0;
          this.minutes = parseFloat(matches[6] || '0') || 0;
          this.seconds = parseFloat(matches[7] || '0') || 0;

          /**
           * Gets the duration in minutes.
           *
           * @returns The total number of minutes represented by the object.
           */
          this.toMinutes = function () {
            return (this.days * 24 + this.hours) * 60 + this.minutes + (this.seconds / 60.0);
          };
        }

        this.populateActivity = (activityType, info) => {
          const keys = Object.keys(this[activityType]);
          const activityData = info._embedded.workflowActivities.find(activity => activity.type === activityType);
          keys.forEach(key => {
            if (key === 'filenameConfiguration') {
              this[activityType][key].template = activityData[key].template;
              this.fileName = activityData[key].template;
            } else {
              if (activityData[key]) {
                this[activityType][key] = activityData[key];
              }
            }
          });

          return this[activityType];
        };

        this.activities = function () {
          const activities = [];

          if (this.source === 'MFP') {
            activities.push(this.SCAN_FILES);
          } else {
            activities.push(this.UPLOAD_FILES);
          }

          if (this.metadataProfile.metadataFields.length || this.INTERACTIVE.metadataFile) {
            activities.push(this.INTERACTIVE);
          }

          if (!this.bypassOutputCreation) {
            activities.push(this[this.fileType]);
          }
          
          activities.push(this[this.destination]);
          
          return activities;
        };

        return this;
      };
    })

    .factory('WorkflowBody', function () {
      return function (formModelData) {
        this.active     = formModelData.active;
        this.name       = formModelData.name;
        this.groups     = formModelData.groups.map(group => group.customProperties.id);
        this.activities = formModelData.activities();
        this.sources    = [formModelData.source];

        if (formModelData.availability === 'ALL') {
          this.groups = [];
        }

        this.activities.forEach(activity => {
          if (activity.type === 'SCAN_FILES') {
            activity.duplexMode = activity.duplexMode ? 'TOP_TO_TOP' : 'SIMPLEX';
            delete activity.paperSource;
          } else if (activity.type === 'UPLOAD_FILES') {
            if (this.sources[0] === 'PHONE') {
              formModelData.imageProcessing = {
                crop: false,
                deleteBlank: false,
                deskew: false,
                rotation: '0'
              };
            }
          } else if (activity.type === 'GENERATE_SEARCHABLE_PDF' || activity.type === 'GENERATE_DOCX') {
            activity.imageProcessing = formModelData.imageProcessing;
            activity.ocrConfiguration = formModelData.isImageOnlyPdf ? null : formModelData.ocrConfiguration;
          } else if (activity.type === 'INTERACTIVE') {
            activity.metadataProfileId = formModelData.metadataProfile.id;
            activity.metadataFile = activity.metadataFile ? { type: activity.metadataFile } : undefined;
          }

          if (activity.timeout) {
            activity.timeout = 'PT' + activity.timeout + 'M';
          }
        });

        return this;
      };
    })

    .factory('WorkflowModel', function (
      $q, $http, GroupModel, WorkflowFormModel, PDF_COMPATIBILITY,
      PDF_IMAGE_QUALITY, ORIENTATION, COLOR, PAPER_SIZE, IMAGE_ROTATION,
      WorkflowBody, Utils
    ) {
      return function (data, OCR_LANGUAGES) {

        this.info = { ...data };
        this.info.groups = [];
        this.id = data._links.self.href.split('/').pop();
        this.customProperties = {
          id: data._links.self.href.split('/').pop()
        };
        this.options = {
          isSelected: false
        };
        this.formModel = null;

        this.tooltipData = {
          source: null,
          destination: null,
          fileType: null,
          groups: null,
          fileName: null,
          imageProcessing: null
        };

        this.appendGroups = function (groups) {
          this.info.groups = groups._embedded['px:groupResources'].map(function (group) {
            return new GroupModel(group);
          });
        };

        this.getGroups = function () {
          const deferred = $q.defer();
          const url = this.info._links['px:groups'].href;
          $http.get(url).then(function (response) {
            if (response.data._embedded) {
              this.appendGroups(response.data);
            } else {
              this.info.groups = [];
            }

            this.buildFormModel();

            deferred.resolve(response);
          }.bind(this)).catch(function (error) {
            deferred.reject(error);
          });
          return deferred.promise;
        };

        this.deleteWorkflow = function () {
          const deferred = $q.defer();
          const url = this.info._links.self.href;

          $http.delete(url).then(function () {
            deferred.resolve();
          }).catch(function (error) {
            deferred.reject(error);
          });
          return deferred.promise;
        };

        this.buildFormModel = () => {
          this.formModel = new WorkflowFormModel();
          this.formModel.setData(this.info);
          this.setTooltipData();
        };

        this.setTooltipData = function () {

          this.tooltipData.destination = angular.copy(this.formModel[this.formModel.destination]);
          this.tooltipData.fileType = angular.copy(this.formModel[this.formModel.fileType]);
          this.tooltipData.groups = angular.copy(this.formModel.groups);
          this.tooltipData.fileName = angular.copy(this.formModel.fileName);
          this.tooltipData.imageProcessing = {};

          switch (this.formModel.fileType) {
            case 'GENERATE_SEARCHABLE_PDF':
              this.tooltipData.fileType.pdfCompatibility = PDF_COMPATIBILITY.find(item => item.id === this.tooltipData.fileType.pdfCompatibility).name;
              this.tooltipData.fileType.imageQuality = PDF_IMAGE_QUALITY.find(item => item.id === this.tooltipData.fileType.imageQuality).name;
              if (!this.formModel.isImageOnlyPdf) {
                this.tooltipData.fileType.ocrConfiguration = angular.copy(this.formModel.ocrConfiguration);
                this.tooltipData.fileType.ocrConfiguration.languages = this.tooltipData.fileType.ocrConfiguration.languages.map(language => {
                  return OCR_LANGUAGES.find(ocr => ocr.id === language).name;
                });
              }
              break;
            case 'GENERATE_DOCX':
              this.tooltipData.fileType.imageQuality = PDF_IMAGE_QUALITY.find(item => item.id === this.tooltipData.fileType.imageQuality).name;
              this.tooltipData.fileType.ocrConfiguration = angular.copy(this.formModel.ocrConfiguration);
              this.tooltipData.fileType.ocrConfiguration.languages = this.tooltipData.fileType.ocrConfiguration.languages.map(language => {
                return OCR_LANGUAGES.find(ocr => ocr.id === language).name;
              });
              break;
          }

          switch (this.formModel.source) {
            case 'MFP':
              this.tooltipData.source = angular.copy(this.formModel.SCAN_FILES);
              this.tooltipData.source.pageOrientation = ORIENTATION.find(item => item.id === this.tooltipData.source.pageOrientation).name;
              this.tooltipData.source.paperSize = PAPER_SIZE.find(item => item.id === this.tooltipData.source.paperSize).name;
              this.tooltipData.source.color = COLOR.find(item => item.id === this.tooltipData.source.color).name;
              break;
            case 'PHONE':
              this.tooltipData.source = { type: 'PHONE' };
              break;
                // case 'COMPUTER':
                //   this.tooltipData.source = { type: 'COMPUTER' }
                // break;
          }

          this.tooltipData.imageProcessing.rotation = IMAGE_ROTATION.find(item => item.id === this.formModel.imageProcessing.rotation).name;
          this.tooltipData.imageProcessing.crop = this.formModel.imageProcessing.crop ? 'Yes' : 'No';
          this.tooltipData.imageProcessing.deskew = this.formModel.imageProcessing.deskew ? 'Yes' : 'No';
          this.tooltipData.imageProcessing.deleteBlank = this.formModel.imageProcessing.deleteBlank ? 'Yes' : 'No';
        };

        this.updateWorkflow = function (originalWorkflowFormModel) {
          const deferred = $q.defer();

          const originalBody = new WorkflowBody(angular.copy(originalWorkflowFormModel));
          const body = new WorkflowBody(angular.copy(this.formModel));

          const patchObjectArray = Utils.getObjectsDifferencesForPatching(originalBody, body);
          if (patchObjectArray.length <= 0) {
            deferred.resolve('No changes to save');
            return deferred.promise;
          }

          const url = this.info._links.self.href;
          $http.put(url, body).then(function (response) {
            deferred.resolve(response);
          }).catch(function (error) {
            deferred.reject(error);
          });
          return deferred.promise;
        };

        return this;
      };
    })

    .factory('WorkflowPagingParams', function (DEFAULT_ITEMS_PER_PAGE) {
      return function () {

        this.pagingCriteria = {
          page: 0,
          pageSize: DEFAULT_ITEMS_PER_PAGE,
          direction: 'ASC',
          sort: 'NAME',
          match: null,
          sources: null,
          destination: null,
          active: null
        };

        this.getPageParams = function () {
          let params =
          '?page=' + this.pagingCriteria.page +
          '&pageSize=' + this.pagingCriteria.pageSize +
          '&direction=' + this.pagingCriteria.direction +
          '&sort=' + this.pagingCriteria.sort;

          if (this.pagingCriteria.match) {
            params += '&match=' + this.pagingCriteria.match;
          }
          if (this.pagingCriteria.sources) {
            params += '&sources=' + this.pagingCriteria.sources;
          }
          if (this.pagingCriteria.destination) {
            params += '&destination=' + this.pagingCriteria.destination;
          }
          if (this.pagingCriteria.active !== null) {
            params += '&active=' + this.pagingCriteria.active;
          }

          params += '&includeWorkflowsWithMetadata=true'; // TODO: keep this line only until a filter is available

          return params;
        };

        return this;
      };
    })

    .service('WorkflowService', function ($http, $q, TenantListService, WorkflowModel, WorkflowBody, EnumerationService, $filter) {
      const OCR_LANGUAGES = EnumerationService.enums.OCRLanguage.map(OCRLanguage => {
        return {
          id: OCRLanguage.id,
          isSelected: false,
          name: ((nameString) => {
            const readableName = nameString.replace(/_/g, ' ');
            return readableName.charAt(0).toUpperCase() + readableName.substr(1).toLowerCase();
          })(OCRLanguage.name)
        };
      });

      OCR_LANGUAGES.forEach((OCRLanguage, index) => {
        if (OCRLanguage.id === 'AUTO') {
          OCRLanguage.name = $filter('translate')('SelectAutomatically');
          OCR_LANGUAGES.splice(index, 1);
          OCR_LANGUAGES.unshift(OCRLanguage);
        }
      });

      this.getWorkflowById = function (workflowId) {
        const deferred = $q.defer();
        const url = `${TenantListService.currentTenant.info._links['px:workflows'].href}/${workflowId}`;
        $http.get(url).then(function (response) {
          const workflow = new WorkflowModel(response.data, OCR_LANGUAGES);
          workflow.getGroups().then(function () {
            deferred.resolve(workflow);
          }).catch(function () {
            deferred.resolve(workflow);
          });
        }).catch(function (error) {
          deferred.reject(error);
        });
        return deferred.promise;
      };

      this.getWorkflowList = function (workflowPagingParams) {
        const deferred = $q.defer();
        const url = TenantListService.currentTenant.info._links['px:workflows'].href  + workflowPagingParams.getPageParams();

        $http.get(url).then(function (response) {
          const workflowListObject = {
            page: response.data.page,
            _links: response.data._links,
            list: []
          };

          if (response.data._embedded) {
            response.data._embedded['px:workflowResources'].forEach((response, index) => {
              const workflow = new WorkflowModel(response, OCR_LANGUAGES);
              workflow.buildFormModel();
              workflowListObject.list.push(workflow);
            });
          }

          deferred.resolve(workflowListObject);

        }).catch(function (error) {
          deferred.reject(error);
        });
        return deferred.promise;
      };

      this.deleteSelected = function (selectedWorkflows, callback) {
        if (selectedWorkflows.length > 0) {
          angular.forEach(selectedWorkflows, function (workflow) {
            workflow.deleteWorkflow();
          });
          if (callback) {
            callback();
          }
        }
      };

      this.createWorkflow = function (workflowFormModel) {
        const deferred = $q.defer();
        const url = TenantListService.currentTenant.info._links['px:workflows'].href;
        const body = new WorkflowBody(angular.copy(workflowFormModel));

        $http.post(url, body).then(function (response) {
          deferred.resolve(response);
        }).catch(function (error) {
          deferred.reject(error);
        });
        return deferred.promise;
      };

      return this;
    });

})();
